import type { FC, SVGProps } from "react";
import type { ScrewForSettings } from "../../../../http/deckDesigner/types/screws";
import { SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM } from "../../../../http/deckDesigner/types/substructureMaterials";
import { getPositiveNumber } from "../deckGeometry";

export type MillimetersToPixels = (millimeters: number) => number;

export type KkScrewSize = Pick<ScrewForSettings, "length" | "screwHeadHeight" | "screwDiameter">;
export type KkScrewVariant = "kkt" | "kka";

type KkScrewInput = Partial<KkScrewSize> & {
  screwFamily?: {
    substructureMaterials?: {
      code?: string | null;
    }[];
  } | null;
};

type KkScrewLayoutParameters = {
  screw?: KkScrewInput | null;
  visibleLength?: number | null;
  showHead?: boolean;
  rotation?: 0 | 180;
  toPixels: MillimetersToPixels;
  centerX: number;
  substructureMaterialCode?: string | null;
  variant?: KkScrewVariant | null;
  anchor:
    | {
        type: "headBottom";
        y: number;
      }
    | {
        type: "top";
        y: number;
      };
};

type KkScrewProps = SVGProps<SVGSVGElement> & {
  screw?: KkScrewInput | null;
  visibleLength?: number | null;
  showHead?: boolean;
  rotation?: 0 | 180;
  toPixels?: MillimetersToPixels;
  centerX?: number;
  headBottomY?: number;
  topY?: number;
  substructureMaterialCode?: string | null;
  variant?: KkScrewVariant | null;
};

export type KkScrewArtwork = {
  bodyDiameter: number;
  headEndX: number;
  sourceHeight: number;
  sourceWidth: number;
  tipStartX: number;
  url: string;
};

export type KkScrewLayout = {
  artwork: KkScrewArtwork;
  bodyHeight: number;
  headHeight: number;
  height: number;
  rotation: 0 | 180;
  shaftHeight: number;
  showHead: boolean;
  tipHeight: number;
  viewBox: string;
  width: number;
  x: number;
  y: number;
};

const KKT_SCREW_URL = new URL("./KkScrewKkt.svg", import.meta.url).href;
const KKA_SCREW_URL = new URL("./KkScrewKka.svg", import.meta.url).href;

const SCREW_ARTWORKS: Record<KkScrewVariant, KkScrewArtwork> = {
  kkt: {
    bodyDiameter: 12.57,
    headEndX: 14.78,
    sourceHeight: 16.8,
    sourceWidth: 143.49,
    tipStartX: 130.02,
    url: KKT_SCREW_URL,
  },
  kka: {
    bodyDiameter: 13.92,
    headEndX: 10.48,
    sourceHeight: 19.46,
    sourceWidth: 153.23,
    tipStartX: 149.62,
    url: KKA_SCREW_URL,
  },
};

export const createMillimetersToPixels =
  (pixelsPerMillimeter: number): MillimetersToPixels =>
  (millimeters: number): number =>
    millimeters * pixelsPerMillimeter;

export const getKkScrewVariant = (
  screw: KkScrewInput | null | undefined,
  substructureMaterialCode?: string | null,
  variant?: KkScrewVariant | null,
): KkScrewVariant => {
  if (variant) {
    return variant;
  }

  const screwMaterials = screw?.screwFamily?.substructureMaterials;
  const isAluminium = screwMaterials?.length
    ? screwMaterials.some((m) => m.code === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM)
    : substructureMaterialCode === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM;

  return isAluminium ? "kka" : "kkt";
};

const getScrewSize = (screw: KkScrewInput | null | undefined): KkScrewSize => ({
  length: getPositiveNumber(screw?.length, 45),
  screwHeadHeight: getPositiveNumber(screw?.screwHeadHeight, 2.5),
  screwDiameter: getPositiveNumber(screw?.screwDiameter, 5),
});

export const getKkScrewLayout = ({
  screw,
  visibleLength,
  showHead = true,
  rotation = 0,
  toPixels,
  centerX,
  substructureMaterialCode,
  variant,
  anchor,
}: KkScrewLayoutParameters): KkScrewLayout => {
  const screwSize = getScrewSize(screw);
  const artwork = SCREW_ARTWORKS[getKkScrewVariant(screw, substructureMaterialCode, variant)];
  const defaultBodyLength = showHead ? screwSize.length : screwSize.length - screwSize.screwHeadHeight;
  const bodyLength = getPositiveNumber(visibleLength, defaultBodyLength);
  const headHeight = showHead ? toPixels(screwSize.screwHeadHeight) : 0;
  const bodyHeight = toPixels(bodyLength);
  const bodyScale = toPixels(screwSize.screwDiameter) / artwork.bodyDiameter;
  const width = artwork.sourceHeight * bodyScale;
  const sourceTipLength = artwork.sourceWidth - artwork.tipStartX;
  const tipHeight = Math.min(bodyHeight, sourceTipLength * bodyScale);
  const shaftHeight = Math.max(bodyHeight - tipHeight, 0);
  const height = headHeight + bodyHeight;
  const y = anchor.type === "headBottom" ? anchor.y - headHeight : anchor.y;

  return {
    artwork,
    bodyHeight,
    height,
    headHeight,
    rotation,
    shaftHeight,
    showHead,
    tipHeight,
    viewBox: `0 0 ${width} ${height}`,
    width,
    x: centerX - width / 2,
    y,
  };
};

type ScrewArtworkSegmentProps = {
  artwork: KkScrewArtwork;
  height: number;
  preserveAspectRatio?: SVGProps<SVGSVGElement>["preserveAspectRatio"];
  sourceEndX: number;
  sourceStartX: number;
  width: number;
  x: number;
  y: number;
};

const ScrewArtworkSegment: FC<ScrewArtworkSegmentProps> = ({
  artwork,
  height,
  preserveAspectRatio = "none",
  sourceEndX,
  sourceStartX,
  width,
  x,
  y,
}) => {
  const sourceLength = sourceEndX - sourceStartX;

  if (width <= 0 || height <= 0 || sourceLength <= 0) {
    return null;
  }

  return (
    <svg
      x={x}
      y={y}
      width={width}
      height={height}
      viewBox={`0 0 ${artwork.sourceHeight} ${sourceLength}`}
      overflow="hidden"
      preserveAspectRatio={preserveAspectRatio}
    >
      <g transform={`matrix(0 1 -1 0 ${artwork.sourceHeight} ${-sourceStartX})`}>
        <image
          href={artwork.url}
          x="0"
          y="0"
          width={artwork.sourceWidth}
          height={artwork.sourceHeight}
          preserveAspectRatio="none"
        />
      </g>
    </svg>
  );
};

const FullScrewArtwork: FC<{ artwork: KkScrewArtwork }> = ({ artwork }) => (
  <g transform={`matrix(0 1 -1 0 ${artwork.sourceHeight} 0)`}>
    <image
      href={artwork.url}
      x="0"
      y="0"
      width={artwork.sourceWidth}
      height={artwork.sourceHeight}
      preserveAspectRatio="none"
    />
  </g>
);

const KkScrew: FC<KkScrewProps> = ({
  screw,
  visibleLength,
  showHead = true,
  rotation = 0,
  toPixels,
  centerX,
  headBottomY,
  topY,
  substructureMaterialCode,
  variant,
  x,
  y,
  width,
  height,
  viewBox,
  preserveAspectRatio,
  ...props
}) => {
  const layout =
    toPixels && centerX != null && (headBottomY != null || topY != null)
      ? getKkScrewLayout({
          screw,
          visibleLength,
          showHead,
          rotation,
          toPixels,
          centerX,
          substructureMaterialCode,
          variant,
          anchor: headBottomY != null ? { type: "headBottom", y: headBottomY } : { type: "top", y: topY ?? 0 },
        })
      : null;
  const artwork = layout?.artwork ?? SCREW_ARTWORKS[getKkScrewVariant(screw, substructureMaterialCode, variant)];
  const layoutTransform = layout?.rotation === 180 ? `rotate(180 ${layout.width / 2} ${layout.height / 2})` : undefined;
  const defaultViewBox = `0 0 ${artwork.sourceHeight} ${artwork.sourceWidth}`;
  const bodyY = layout?.headHeight ?? 0;
  const tipY = bodyY + (layout?.shaftHeight ?? 0);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={layout?.viewBox ?? viewBox ?? defaultViewBox}
      aria-hidden="true"
      x={layout?.x ?? x}
      y={layout?.y ?? y}
      width={layout?.width ?? width}
      height={layout?.height ?? height}
      overflow="visible"
      preserveAspectRatio={layout ? "none" : preserveAspectRatio}
      {...props}
    >
      {layout ? (
        <g transform={layoutTransform}>
          {layout.showHead && (
            <ScrewArtworkSegment
              artwork={artwork}
              x={0}
              y={0}
              width={layout.width}
              height={layout.headHeight}
              sourceStartX={0}
              sourceEndX={artwork.headEndX}
            />
          )}
          <ScrewArtworkSegment
            artwork={artwork}
            x={0}
            y={bodyY}
            width={layout.width}
            height={layout.shaftHeight}
            sourceStartX={artwork.headEndX}
            sourceEndX={artwork.tipStartX}
          />
          <ScrewArtworkSegment
            artwork={artwork}
            x={0}
            y={tipY}
            width={layout.width}
            height={layout.tipHeight}
            sourceStartX={artwork.tipStartX}
            sourceEndX={artwork.sourceWidth}
            preserveAspectRatio={layout.tipHeight === layout.bodyHeight ? "none" : "xMidYMax meet"}
          />
        </g>
      ) : (
        <FullScrewArtwork artwork={artwork} />
      )}
    </svg>
  );
};

export default KkScrew;
